import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const Header = ({ currentPath, categories }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [hoveredPath, setHoveredPath] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenCategories, setMobileOpenCategories] = useState([]);
  const dropdownRef = useRef(null);
  const closeTimeout = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
        setHoveredPath([]);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCategoryEnter = (categoryName) => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
    }
    setOpenDropdown(categoryName);
    setHoveredPath([]);
  };

  const handleCategoryLeave = () => {
    closeTimeout.current = setTimeout(() => {
      setOpenDropdown(null);
      setHoveredPath([]);
    }, 300);
  };

  const handleSubmenuEnter = (subId, parentPath = []) => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
    }
    const newPath = [...parentPath, subId];
    setHoveredPath(newPath);
  };

  const handleSubmenuLeave = () => {
    closeTimeout.current = setTimeout(() => {
      setHoveredPath([]);
    }, 100);
  };

  // Check if a submenu should be open based on the hovered path
  const isSubmenuOpen = (subId, parentPath = []) => {
    const expectedPath = [...parentPath, subId];
    return expectedPath.length <= hoveredPath.length && 
           expectedPath.every((id, index) => hoveredPath[index] === id);
  };

  // Toggle mobile category expansion
  const toggleMobileCategory = (categoryId) => {
    setMobileOpenCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Recursive function to render mobile subcategories
  const renderMobileSubcategories = (subcategories, parentPath = []) => {
    return subcategories.map((sub) => {
      const fullPath = [...parentPath, sub.id];
      const isOpen = mobileOpenCategories.includes(fullPath.join('-'));
      
      return (
        <div key={sub.id} className="pl-4">
          {sub.subcategories?.length > 0 ? (
            <div>
              <button
                onClick={() => toggleMobileCategory(fullPath.join('-'))}
                className="flex items-center justify-between w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              >
                <span>{sub.name}</span>
                <svg 
                  className={`ml-2 w-4 h-4 transform transition-transform ${isOpen ? 'rotate-90' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {isOpen && (
                <div className="pl-4">
                  {renderMobileSubcategories(sub.subcategories, fullPath)}
                </div>
              )}
            </div>
          ) : (
            <a
              href={sub.is_leaf ? `/subcategories/${sub.id}` : `/?subcategory=${sub.id}`}
              className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              {sub.name}
            </a>
          )}
        </div>
      );
    });
  };

  // Recursive function to render subcategories at any depth
  const renderSubcategories = (subcategories, parentPath = [], level = 1) => {
    return subcategories.map((sub) => (
      <div 
        key={sub.id} 
        className="relative"
        onMouseEnter={() => handleSubmenuEnter(sub.id, parentPath)}
        onMouseLeave={handleSubmenuLeave}
      >
        <a
          href={sub.is_leaf ? `/subcategories/${sub.id}` : `/?subcategory=${sub.id}`}
          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between group"
          role="menuitem"
        >
          <span>{sub.name}</span>
          {sub.subcategories?.length > 0 && (
            <svg 
              className="ml-2 w-4 h-4 text-gray-400 group-hover:text-gray-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          )}
        </a>

        {sub.subcategories?.length > 0 && isSubmenuOpen(sub.id, parentPath) && (
          <div 
            className={`absolute left-full top-0 mt-0 -ml-px w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5`}
            style={{ zIndex: 50 + level }}
          >
            <div className="py-1">
              {renderSubcategories(sub.subcategories, [...parentPath, sub.id], level + 1)}
            </div>
          </div>
        )}
      </div>
    ));
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-gray-200 z-40 shadow-[0_1px_0_rgba(0,148,217,0.12),0_4px_20px_-8px_rgba(0,148,217,0.18)]">
      {/* thin brand accent line */}
      <div className="h-0.5 w-full bg-gradient-to-r from-brand-700 via-brand-500 to-brand-300" aria-hidden="true"></div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <div className="flex-shrink-0 flex items-center">
            <a
              href="/"
              className="flex items-center group"
              aria-label="RALMAQ — página inicial"
            >
              <img
                src="/ralmaq-logo.png?v=3"
                alt="RALMAQ"
                className="h-9 md:h-10 w-auto max-w-none shrink-0 object-contain transition-transform duration-300 group-hover:scale-[1.02]"
              />
            </a>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex space-x-8" ref={dropdownRef}>
            {categories.map((category) => {
              return (
                <div
                  key={category.id}
                  className="relative"
                  onMouseEnter={() => handleCategoryEnter(category.name)}
                  onMouseLeave={handleCategoryLeave}
                >
                  <a
                    href={`/?category=${category.id}`}
                    className={`inline-flex items-center px-1 pt-1 text-sm font-semibold uppercase tracking-wide border-b-2 transition-colors ${
                      openDropdown === category.name
                        ? 'border-brand-500 text-brand-ink'
                        : 'border-transparent text-gray-600 hover:border-brand-300 hover:text-brand-700'
                    }`}
                  >
                    {category.name}
                    {category.subcategories?.length > 0 && (
                      <svg 
                        className={`ml-1 w-4 h-4 transform transition-transform ${openDropdown === category.name ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </a>

                  {category.subcategories?.length > 0 && openDropdown === category.name && (
                    <div 
                      className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
                    >
                      <div className="py-1" role="menu">
                        {renderSubcategories(category.subcategories)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-brand-700 hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500"
            >
              <span className="sr-only">{mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}</span>
              {mobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Contact Button */}
          <div className="hidden md:flex items-center">
  
          </div>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="pt-2 pb-3 space-y-1">
            {categories.map((category) => {
              const isOpen = mobileOpenCategories.includes(category.id.toString());
              
              return (
                <div key={category.id}>
                  {category.subcategories?.length > 0 ? (
                    <div>
                      <button
                        onClick={() => toggleMobileCategory(category.id.toString())}
                        className="flex items-center justify-between w-full text-left px-4 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                      >
                        <span>{category.name}</span>
                        <svg 
                          className={`ml-2 w-4 h-4 transform transition-transform ${isOpen ? 'rotate-90' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      {isOpen && (
                        <div className="bg-gray-50">
                          {renderMobileSubcategories(category.subcategories, [category.id])}
                        </div>
                      )}
                    </div>
                  ) : (
                    <a
                      href={`/?category=${category.id}`}
                      className="block px-4 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {category.name}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
};

// Define recursive PropType for unlimited nesting
const subcategoryShape = {
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  subcategories: PropTypes.array
};

subcategoryShape.subcategories = PropTypes.arrayOf(PropTypes.shape(subcategoryShape));

Header.propTypes = {
  currentPath: PropTypes.string,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      subcategories: PropTypes.arrayOf(PropTypes.shape(subcategoryShape))
    })
  ).isRequired
};

Header.defaultProps = {
  currentPath: '/',
  categories: []
};

export default Header;